/* /dashboard/my-mirrors — WYSIWYG block-based mirror builder.
   Click any block on the canvas to edit it inline. Use the left panel
   to add new blocks. The canvas IS the live mirror at visitor size. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, ExternalLink, Save, Copy, Globe2, Type, AlignLeft,
  Heading1, Image as ImageIcon, Youtube, MousePointerClick, Minus,
  ArrowUp, ArrowDown, X, Eye, ArrowLeft, Palette, GripVertical, Upload,
  Monitor, Tablet, Smartphone, Columns, Mail, Star, HelpCircle, LayoutGrid,
} from "lucide-react";
import axios from "axios"; // eslint-disable-line no-unused-vars
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BRAND = "#287F8E";
const GREEN = "#2E9F3A";
const ORANGE = "#F39A1F";
const WINE = "#8B2A2A";

const uid = () => Math.random().toString(36).slice(2, 10);

/* ----------------------------- DEFAULT BLOCK FACTORIES ----------------------------- */
const NEW_BLOCK = {
  heading: () => ({ id: uid(), type: "heading", text: "Big Headline Goes Here",
    fontSize: 56, color: "#0F172A", align: "center", fontFamily: "serif", bold: true, padY: 16 }),
  text: () => ({ id: uid(), type: "text", text: "Click this paragraph to edit it. Type whatever you want here.",
    fontSize: 18, color: "#0F172A", align: "center", fontFamily: "serif", padY: 12 }),
  image: () => ({ id: uid(), type: "image", src: "", widthPct: 60, align: "center", radius: 12, padY: 16 }),
  video: () => ({ id: uid(), type: "video", url: "", padY: 16 }),
  button: () => ({ id: uid(), type: "button", label: "ORDER NOW", url: "",
    bgColor: GREEN, textColor: "#FFFFFF", fontSize: 18, align: "center", padY: 16, openInNew: true }),
  spacer: () => ({ id: uid(), type: "spacer", height: 40 }),
  divider: () => ({ id: uid(), type: "divider", color: "#E5E7EB", thickness: 2, padY: 12 }),
  columns: () => ({ id: uid(), type: "columns", cols: 2, gap: 24, padY: 16,
    columns: [
      [NEW_BLOCK.heading()],
      [{ ...NEW_BLOCK.text(), text: "Side text. Click to edit." }],
    ] }),
  form: () => ({ id: uid(), type: "form", title: "Get The Free Guide",
    description: "Enter your email and we'll send it over.",
    buttonLabel: "Send It To Me",
    bgColor: "#EAF6FC", accentColor: ORANGE, textColor: "#0F172A",
    fields: ["name","email"], padY: 24 }),
  testimonial: () => ({ id: uid(), type: "testimonial",
    quote: "This is what one of your customers said. Click to edit.",
    author: "First Last",
    role: "Customer",
    photoUrl: "",
    bgColor: "#FFFBEF", textColor: "#0F172A", padY: 24 }),
  faq: () => ({ id: uid(), type: "faq",
    items: [
      { q: "First question goes here?", a: "Click to edit the answer." },
      { q: "Second question?", a: "Another answer." },
    ], padY: 16 }),
};

/* ----------------------------- PRE-BUILT SECTION TEMPLATES ----------------------------- */
const SECTION_TEMPLATES = {
  hero: () => ([
    { ...NEW_BLOCK.heading(), text: "Your Big Hero Headline", fontSize: 64, uppercase: true, bold: true },
    { ...NEW_BLOCK.text(), text: "One-line promise that explains what this page does for the visitor.", fontSize: 20 },
    { ...NEW_BLOCK.button(), label: "GET STARTED" },
    NEW_BLOCK.spacer(),
  ]),
  optin: () => ([
    NEW_BLOCK.form(),
  ]),
  testimonial: () => ([
    NEW_BLOCK.testimonial(),
  ]),
  faq: () => ([
    { ...NEW_BLOCK.heading(), text: "Frequently Asked", fontSize: 36 },
    NEW_BLOCK.faq(),
  ]),
  twoCol: () => ([
    {
      ...NEW_BLOCK.columns(),
      columns: [
        [{ ...NEW_BLOCK.image(), src: "" }],
        [
          { ...NEW_BLOCK.heading(), text: "Feature Headline", fontSize: 32, align: "left" },
          { ...NEW_BLOCK.text(), text: "Describe this feature in a sentence or two.", align: "left" },
        ],
      ],
    },
  ]),
};

const EMPTY_MIRROR = {
  slug: "",
  // Legacy fields kept (backend still accepts them) but no longer used by the editor.
  headline: "", tagline: "", photo_url: "",
  free_gift_title: "", free_gift_description: "", free_gift_cta: "",
  upsell_title: "", upsell_description: "", upsell_price: 0,
  upsell_cta_label: "", upsell_cta_url: "",
  bump_title: "", bump_description: "", bump_price: 0, bump_cta_url: "",
  video_url: "", font_family: "serif", headline_size: "md",
  bg_color: "#FFFFFF", accent_color: "#2E9F3A", text_color: "#0F172A",
  blocks: [],
  published: true,
};

const STARTER_BLOCKS = () => [
  NEW_BLOCK.heading(),
  { ...NEW_BLOCK.text(), text: "Short tagline that explains what this page is about." },
  NEW_BLOCK.button(),
];

/* ----------------------------- MAIN COMPONENT ----------------------------- */
export default function MyMirrorsTab() {
  const { user } = useAuth();
  const [mirrors, setMirrors] = useState([]);
  const [busy, setBusy] = useState(true);
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const handle = (user?.affiliate_code || user?.username || "").toLowerCase();

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/mirrors/mine");
      setMirrors(r.data?.mirrors || []);
    } catch { toast.error("Couldn't load your mirrors."); }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEdit({ ...EMPTY_MIRROR, blocks: STARTER_BLOCKS() });
    setCreating(true);
  };
  const startEdit = (m) => {
    setEdit({ ...EMPTY_MIRROR, ...m, blocks: Array.isArray(m.blocks) && m.blocks.length ? m.blocks : STARTER_BLOCKS() });
    setCreating(false);
  };
  const cancel = () => { setEdit(null); setCreating(false); };

  const save = async () => {
    if (!edit) return;
    if (!edit.slug || edit.slug.length < 3) { toast.error("Your link name needs at least 3 characters."); return; }
    try {
      if (creating) {
        await api.post("/mirrors", edit);
        toast.success(`★ Mirror "${edit.slug}" published.`);
      } else {
        await api.put(`/mirrors/${edit.slug}`, edit);
        toast.success("★ Saved.");
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(`Delete /m/${handle}/${slug}? Can't undo.`)) return;
    try { await api.delete(`/mirrors/${slug}`); toast.success("Deleted."); await load(); }
    catch { toast.error("Couldn't delete."); }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Link copied."); };

  if (edit) {
    return (
      <WYSIWYGEditor
        edit={edit}
        setEdit={setEdit}
        save={save}
        cancel={cancel}
        creating={creating}
        handle={handle}
        origin={origin}
      />
    );
  }

  return (
    <div className="space-y-6 p-5 md:p-7" data-testid="my-mirrors-tab">
      <div className="rounded-2xl bg-white border-2 shadow-md p-5 md:p-6" style={{ borderColor: BRAND }}>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-11 h-11 rounded-full flex items-center justify-center text-white shrink-0" style={{ background: BRAND }}>
            <Globe2 className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: BRAND }}>★ MY MIRROR SITES</div>
            <h1 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight" style={{ color: "#0F172A" }}>
              Build As Many Mirrors As You Want.
            </h1>
            <p className="text-sm md:text-base text-foreground/80 mt-1">
              Click into a mirror and edit it like Webstarts — click any block on the page to change it.
              Each one publishes to <span className="font-mono">/m/{handle || "you"}/your-link-name</span>.
            </p>
          </div>
          <button onClick={startNew} data-testid="mirror-new-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition shrink-0"
                  style={{ background: GREEN }}>
            <Plus className="w-4 h-4"/> Build A New Mirror
          </button>
        </div>
      </div>

      {busy ? <div className="text-sm text-foreground/60 px-2">Loading…</div>
       : mirrors.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed p-12 text-center" style={{ borderColor: `${BRAND}66` }}>
          <Globe2 className="w-12 h-12 mx-auto mb-3" style={{ color: BRAND }}/>
          <h3 className="font-serif-display text-2xl uppercase" style={{ color: "#0F172A" }}>No Mirrors Yet</h3>
          <p className="text-sm text-foreground/70 mt-2 max-w-md mx-auto">
            Click <strong>Build A New Mirror</strong> above to start.
          </p>
        </div>
       ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mirror-list">
          {mirrors.map((m) => {
            const url = `${origin}/m/${handle}/${m.slug}`;
            const firstHeading = (m.blocks || []).find((b) => b.type === "heading")?.text || m.headline;
            return (
              <div key={m.slug} className="rounded-xl bg-white border-2 shadow-md overflow-hidden flex flex-col"
                   style={{ borderColor: `${ORANGE}55` }} data-testid={`mirror-card-${m.slug}`}>
                <div className="p-4 flex-1">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: ORANGE }}>
                    /m/{handle}/{m.slug}
                  </div>
                  <div className="font-serif-display text-lg leading-tight" style={{ color: "#0F172A", fontWeight: 700 }}>
                    {firstHeading || <span className="text-foreground/50">No headline yet</span>}
                  </div>
                </div>
                <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: `${ORANGE}33`, background: "#FFFBEF" }}>
                  <button onClick={() => startEdit(m)} data-testid={`mirror-edit-${m.slug}`}
                          className="flex-1 px-3 py-2 rounded text-[10px] uppercase tracking-[0.18em] font-bold text-white"
                          style={{ background: BRAND }}>Edit</button>
                  <button onClick={() => copy(url)} title="Copy link" data-testid={`mirror-copy-${m.slug}`}
                          className="w-9 h-9 rounded text-white flex items-center justify-center"
                          style={{ background: GREEN }}><Copy className="w-4 h-4"/></button>
                  <a href={url} target="_blank" rel="noopener noreferrer" title="Open" data-testid={`mirror-open-${m.slug}`}
                     className="w-9 h-9 rounded text-white flex items-center justify-center"
                     style={{ background: "#0F172A" }}><ExternalLink className="w-4 h-4"/></a>
                  <button onClick={() => remove(m.slug)} title="Delete" data-testid={`mirror-del-${m.slug}`}
                          className="w-9 h-9 rounded text-white flex items-center justify-center"
                          style={{ background: WINE }}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- WYSIWYG EDITOR ----------------------------- */
function WYSIWYGEditor({ edit, setEdit, save, cancel, creating, handle, origin }) {
  const [selectedId, setSelectedId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [device, setDevice] = useState("desktop"); // desktop | tablet | mobile
  const [dragId, setDragId] = useState(null);

  const blocks = edit.blocks || [];
  const selected = findBlockDeep(blocks, selectedId);
  const url = `${origin}/m/${handle}/${edit.slug || "your-link-name"}`;

  const setBlocks = (next) => setEdit((p) => ({ ...p, blocks: typeof next === "function" ? next(p.blocks || []) : next }));

  const addBlock = (type) => {
    const b = NEW_BLOCK[type]();
    setBlocks((prev) => [...prev, b]);
    setSelectedId(b.id);
  };

  const addSection = (key) => {
    const items = SECTION_TEMPLATES[key]();
    setBlocks((prev) => [...prev, ...items]);
    setSelectedId(items[0]?.id || null);
  };

  const updateBlock = (id, patch) =>
    setBlocks((prev) => updateBlockDeep(prev, id, patch));

  const removeBlock = (id) => {
    setBlocks((prev) => removeBlockDeep(prev, id));
    setSelectedId(null);
  };

  const moveBlock = (id, dir) => {
    setBlocks((prev) => moveBlockDeep(prev, id, dir));
  };

  const duplicateBlock = (id) => {
    setBlocks((prev) => duplicateBlockDeep(prev, id));
  };

  const onDropReorder = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setBlocks((prev) => {
      const i = prev.findIndex((b) => b.id === dragId);
      const j = prev.findIndex((b) => b.id === targetId);
      if (i < 0 || j < 0) return prev;
      const copy = [...prev];
      const [item] = copy.splice(i, 1);
      copy.splice(j, 0, item);
      return copy;
    });
    setDragId(null);
  };

  const deviceWidth = device === "mobile" ? 390 : device === "tablet" ? 768 : 980;

  if (previewMode) {
    return (
      <div className="min-h-screen" data-testid="wysiwyg-preview-mode">
        <div className="sticky top-0 z-30 px-4 py-3 flex items-center justify-between border-b shadow gap-2 flex-wrap"
             style={{ background: "#0F172A", color: "#FFFFFF" }}>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: ORANGE }}>
            ★ PREVIEW — exactly what your visitors see
          </div>
          <DeviceToggle device={device} setDevice={setDevice}/>
          <button onClick={() => setPreviewMode(false)} data-testid="wysiwyg-exit-preview"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white"
                  style={{ background: GREEN }}>
            <ArrowLeft className="w-4 h-4"/> Back To Edit
          </button>
        </div>
        <div className="py-6 px-3" style={{ background: "#E2E8F0" }}>
          <div className="mx-auto rounded-lg overflow-hidden shadow-2xl border border-black/10" style={{ maxWidth: deviceWidth, background: edit.bg_color || "#FFFFFF" }}>
            <Canvas page={edit} blocks={blocks} readOnly device={device}/>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="wysiwyg-editor">
      {/* TOP BAR */}
      <div className="sticky top-0 z-30 px-3 py-2 flex items-center gap-2 border-b shadow flex-wrap"
           style={{ background: "#0F172A", color: "#FFFFFF" }}>
        <button onClick={cancel} data-testid="wysiwyg-cancel"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white border-2"
                style={{ borderColor: "#FFFFFF55" }}>
          <ArrowLeft className="w-4 h-4"/> Back
        </button>
        <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: ORANGE }}>
          ★ {creating ? "NEW MIRROR" : "EDIT MIRROR"}
        </div>
        <div className="hidden md:block text-xs font-mono opacity-80" style={{ color: "#EAF6FC" }}>{url}</div>
        <div className="flex-1"/>
        <DeviceToggle device={device} setDevice={setDevice}/>
        <SlugInput
          value={edit.slug}
          onChange={(v) => setEdit((p) => ({ ...p, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
          disabled={!creating}
        />
        <button onClick={() => setPreviewMode(true)} data-testid="wysiwyg-preview-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white"
                style={{ background: "#287F8E" }}>
          <Eye className="w-4 h-4"/> Preview
        </button>
        <button onClick={save} data-testid="wysiwyg-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white"
                style={{ background: GREEN }}>
          <Save className="w-4 h-4"/> {creating ? "Publish" : "Save"}
        </button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr_300px]">
        {/* LEFT: ADD BLOCKS + SECTIONS */}
        <aside className="border-r bg-[#F8FAFC] p-3 lg:sticky lg:top-[52px] lg:h-[calc(100vh-52px)] lg:overflow-y-auto"
               data-testid="wysiwyg-add-panel">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: BRAND }}>
            ★ Add A Block
          </div>
          <div className="grid grid-cols-2 gap-2">
            <AddBtn icon={<Heading1 className="w-5 h-5"/>} label="Heading" onClick={() => addBlock("heading")} testid="add-heading"/>
            <AddBtn icon={<AlignLeft className="w-5 h-5"/>} label="Paragraph" onClick={() => addBlock("text")} testid="add-text"/>
            <AddBtn icon={<ImageIcon className="w-5 h-5"/>} label="Image" onClick={() => addBlock("image")} testid="add-image"/>
            <AddBtn icon={<Youtube className="w-5 h-5"/>} label="Video" onClick={() => addBlock("video")} testid="add-video"/>
            <AddBtn icon={<MousePointerClick className="w-5 h-5"/>} label="Button" onClick={() => addBlock("button")} testid="add-button"/>
            <AddBtn icon={<Columns className="w-5 h-5"/>} label="Columns" onClick={() => addBlock("columns")} testid="add-columns"/>
            <AddBtn icon={<Mail className="w-5 h-5"/>} label="Opt-In Form" onClick={() => addBlock("form")} testid="add-form"/>
            <AddBtn icon={<Star className="w-5 h-5"/>} label="Testimonial" onClick={() => addBlock("testimonial")} testid="add-testimonial"/>
            <AddBtn icon={<HelpCircle className="w-5 h-5"/>} label="FAQ" onClick={() => addBlock("faq")} testid="add-faq"/>
            <AddBtn icon={<Minus className="w-5 h-5"/>} label="Divider" onClick={() => addBlock("divider")} testid="add-divider"/>
            <AddBtn icon={<Type className="w-5 h-5"/>} label="Spacer" onClick={() => addBlock("spacer")} testid="add-spacer"/>
          </div>

          <div className="mt-5 pt-4 border-t">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2 flex items-center gap-1.5" style={{ color: ORANGE }}>
              <LayoutGrid className="w-3.5 h-3.5"/> Pre-Built Sections
            </div>
            <div className="grid grid-cols-1 gap-1.5">
              <SectionBtn label="Hero (headline + button)" onClick={() => addSection("hero")} testid="sec-hero"/>
              <SectionBtn label="2-Column (image + text)" onClick={() => addSection("twoCol")} testid="sec-twocol"/>
              <SectionBtn label="Opt-In Form" onClick={() => addSection("optin")} testid="sec-optin"/>
              <SectionBtn label="Testimonial" onClick={() => addSection("testimonial")} testid="sec-testimonial"/>
              <SectionBtn label="FAQ" onClick={() => addSection("faq")} testid="sec-faq"/>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2 flex items-center gap-1.5" style={{ color: BRAND }}>
              <Palette className="w-3.5 h-3.5"/> Page Background
            </div>
            <SwatchRow
              value={edit.bg_color}
              onChange={(v) => setEdit((p) => ({ ...p, bg_color: v }))}
              options={["#FFFFFF","#EAF6FC","#FFFBEF","#F5F3EE","#0F172A","#FFE8E8"]}
              testid="page-bg"
            />
            <input type="color" value={edit.bg_color || "#FFFFFF"}
                   onChange={(e) => setEdit((p) => ({ ...p, bg_color: e.target.value.toUpperCase() }))}
                   className="mt-2 w-full h-9 rounded cursor-pointer"
                   data-testid="page-bg-picker"/>
          </div>

          <div className="mt-5 text-[11px] text-foreground/60 leading-snug">
            <strong>How to use:</strong> Click any block on the page to select. Click again to edit text. Drag the grip to reorder.
          </div>
        </aside>

        {/* CENTER: CANVAS */}
        <main className="overflow-y-auto" style={{ background: "#E2E8F0" }} data-testid="wysiwyg-canvas">
          <div className="py-6 px-3 md:px-6">
            <div className="text-center text-[10px] tracking-[0.28em] uppercase font-bold mb-3" style={{ color: "#475569" }}>
              ★ /m/{handle || "you"}/{edit.slug || "your-link-name"} — {device.toUpperCase()} view
            </div>
            <div className="mx-auto rounded-lg overflow-hidden shadow-2xl border border-black/10 transition-all"
                 style={{ maxWidth: deviceWidth, background: edit.bg_color || "#FFFFFF" }}>
              <Canvas
                page={edit}
                blocks={blocks}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                updateBlock={updateBlock}
                removeBlock={removeBlock}
                moveBlock={moveBlock}
                duplicateBlock={duplicateBlock}
                dragId={dragId}
                setDragId={setDragId}
                onDropReorder={onDropReorder}
                device={device}
              />
              {blocks.length === 0 && (
                <div className="py-20 text-center text-foreground/50">
                  Empty page. Click &quot;Add A Block&quot; on the left to begin.
                </div>
              )}
            </div>
          </div>
        </main>

        {/* RIGHT: SELECTED BLOCK PROPERTIES */}
        <aside className="border-l bg-white p-3 lg:sticky lg:top-[52px] lg:h-[calc(100vh-52px)] lg:overflow-y-auto"
               data-testid="wysiwyg-props-panel">
          {selected ? (
            <BlockProps block={selected} onChange={(patch) => updateBlock(selected.id, patch)}/>
          ) : (
            <div className="text-center py-10 px-3">
              <MousePointerClick className="w-10 h-10 mx-auto mb-2 text-foreground/30"/>
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: BRAND }}>
                ★ Click A Block
              </div>
              <p className="text-xs text-foreground/60 mt-2">
                Click any block on the page to change colors, sizes, links, and more.
              </p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function DeviceToggle({ device, setDevice }) {
  const opts = [
    { v: "desktop", icon: <Monitor className="w-4 h-4"/> },
    { v: "tablet",  icon: <Tablet className="w-4 h-4"/> },
    { v: "mobile",  icon: <Smartphone className="w-4 h-4"/> },
  ];
  return (
    <div className="flex items-center gap-1 bg-white/10 rounded p-0.5" data-testid="device-toggle">
      {opts.map((o) => (
        <button key={o.v} onClick={() => setDevice(o.v)} data-testid={`device-${o.v}`}
                title={o.v}
                className="w-8 h-8 rounded flex items-center justify-center"
                style={{ background: device === o.v ? ORANGE : "transparent", color: "#FFFFFF" }}>
          {o.icon}
        </button>
      ))}
    </div>
  );
}

function SectionBtn({ label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="text-left px-3 py-2 rounded border-2 bg-white text-xs font-bold hover:shadow"
            style={{ borderColor: `${ORANGE}55`, color: "#0F172A" }}>
      + {label}
    </button>
  );
}

/* ----------------------------- CANVAS ----------------------------- */
function Canvas({ page, blocks, selectedId, setSelectedId, updateBlock, removeBlock, moveBlock, duplicateBlock, dragId, setDragId, onDropReorder, readOnly, device }) {
  return (
    <div style={{ background: page.bg_color || "#FFFFFF" }} className="min-h-[60vh]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        {(blocks || []).map((b) => (
          <BlockShell
            key={b.id}
            block={b}
            selected={!readOnly && selectedId === b.id}
            onSelect={readOnly ? undefined : () => setSelectedId(b.id)}
            onUpdate={readOnly ? undefined : (patch) => updateBlock(b.id, patch)}
            onRemove={readOnly ? undefined : () => removeBlock(b.id)}
            onMove={readOnly ? undefined : (dir) => moveBlock(b.id, dir)}
            onDuplicate={readOnly ? undefined : () => duplicateBlock(b.id)}
            onDragStart={readOnly ? undefined : () => setDragId(b.id)}
            onDropOver={readOnly ? undefined : () => onDropReorder(b.id)}
            isDragging={!readOnly && dragId === b.id}
            readOnly={readOnly}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            updateBlock={updateBlock}
            device={device}
          />
        ))}
      </div>
    </div>
  );
}

function BlockShell({ block, selected, onSelect, onUpdate, onRemove, onMove, onDuplicate, onDragStart, onDropOver, isDragging, readOnly, selectedId, setSelectedId, updateBlock, device }) {
  const ring = selected ? `2px solid ${ORANGE}` : "2px solid transparent";
  return (
    <div
      className={`relative group ${readOnly ? "" : "cursor-pointer"} ${isDragging ? "opacity-40" : ""}`}
      style={{ outline: ring, outlineOffset: 4, borderRadius: 6, paddingTop: block.padY ?? 8, paddingBottom: block.padY ?? 8 }}
      onClick={(e) => { if (onSelect) { e.stopPropagation(); onSelect(); } }}
      draggable={!readOnly}
      onDragStart={(e) => { if (onDragStart) { e.dataTransfer.effectAllowed = "move"; onDragStart(); } }}
      onDragOver={(e) => { if (!readOnly) e.preventDefault(); }}
      onDrop={(e) => { if (onDropOver) { e.preventDefault(); e.stopPropagation(); onDropOver(); } }}
      data-testid={`block-${block.type}-${block.id}`}
    >
      {!readOnly && (
        <div className="absolute -left-7 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition cursor-grab active:cursor-grabbing"
             title="Drag to reorder"
             onClick={(e) => e.stopPropagation()}>
          <GripVertical className="w-5 h-5 text-foreground/40"/>
        </div>
      )}
      {!readOnly && selected && (
        <div className="absolute -top-9 right-0 flex items-center gap-1 bg-[#0F172A] text-white rounded-md px-1.5 py-1 shadow z-10" onClick={(e) => e.stopPropagation()}>
          <BtnSm title="Move up" onClick={() => onMove(-1)} testid="block-up"><ArrowUp className="w-3.5 h-3.5"/></BtnSm>
          <BtnSm title="Move down" onClick={() => onMove(1)} testid="block-down"><ArrowDown className="w-3.5 h-3.5"/></BtnSm>
          <BtnSm title="Duplicate" onClick={onDuplicate} testid="block-dup"><Copy className="w-3.5 h-3.5"/></BtnSm>
          <BtnSm title="Delete" onClick={onRemove} testid="block-del" danger><X className="w-3.5 h-3.5"/></BtnSm>
        </div>
      )}
      <RenderBlock
        block={block}
        onUpdate={onUpdate}
        readOnly={readOnly}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        updateBlock={updateBlock}
        device={device}
      />
    </div>
  );
}

function BtnSm({ children, onClick, title, danger, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
            className={`w-7 h-7 rounded flex items-center justify-center ${danger ? "bg-[#8B2A2A]" : "hover:bg-white/15"}`}>
      {children}
    </button>
  );
}

/* ----------------------------- BLOCK RENDERERS ----------------------------- */
function RenderBlock({ block, onUpdate, readOnly, selectedId, setSelectedId, updateBlock, device }) {
  switch (block.type) {
    case "heading":
      return (
        <EditableText
          as="h1"
          value={block.text}
          onChange={(v) => onUpdate && onUpdate({ text: v })}
          readOnly={readOnly}
          style={{
            fontSize: block.fontSize,
            color: block.color,
            textAlign: block.align,
            fontFamily: block.fontFamily === "sans" ? "ui-sans-serif, system-ui, sans-serif" : "Georgia, 'Times New Roman', serif",
            fontWeight: block.bold ? 800 : 600,
            lineHeight: 1.05,
            textTransform: block.uppercase ? "uppercase" : "none",
            margin: 0,
          }}
        />
      );
    case "text":
      return (
        <EditableText
          as="p"
          value={block.text}
          onChange={(v) => onUpdate && onUpdate({ text: v })}
          readOnly={readOnly}
          style={{
            fontSize: block.fontSize,
            color: block.color,
            textAlign: block.align,
            fontFamily: block.fontFamily === "sans" ? "ui-sans-serif, system-ui, sans-serif" : "Georgia, 'Times New Roman', serif",
            lineHeight: 1.5,
            margin: 0,
          }}
        />
      );
    case "image":
      return (
        <div style={{ textAlign: block.align || "center" }}>
          {block.src ? (
            <img src={resolveSrc(block.src)} alt=""
                 style={{ width: `${block.widthPct || 60}%`, borderRadius: block.radius || 0, display: "inline-block", maxWidth: "100%" }}/>
          ) : (
            <div className="inline-block px-6 py-10 border-2 border-dashed rounded-lg text-foreground/50 text-sm" style={{ borderColor: "#94A3B8" }}>
              Click here, then upload or paste an image URL in the right panel.
            </div>
          )}
        </div>
      );
    case "video": {
      const ytId = extractYouTubeId(block.url);
      return ytId ? (
        <div className="mx-auto rounded-lg overflow-hidden shadow bg-black" style={{ aspectRatio: "16/9", maxWidth: 720 }}>
          <iframe src={`https://www.youtube.com/embed/${ytId}?rel=0`} title="Video"
                  className="w-full h-full" style={{ border: 0 }} allowFullScreen/>
        </div>
      ) : (
        <div className="mx-auto text-center px-6 py-10 border-2 border-dashed rounded-lg text-foreground/50 text-sm" style={{ borderColor: "#94A3B8", maxWidth: 720 }}>
          Click here, then paste an UNLISTED YouTube URL in the right panel.
        </div>
      );
    }
    case "button": {
      const inner = (
        <span
          className="inline-flex items-center justify-center px-7 py-3.5 rounded-md font-bold uppercase tracking-[0.18em] shadow"
          style={{ background: block.bgColor, color: block.textColor, fontSize: block.fontSize }}
        >
          <EditableText
            as="span"
            value={block.label}
            onChange={(v) => onUpdate && onUpdate({ label: v })}
            readOnly={readOnly}
            style={{ color: block.textColor, fontSize: block.fontSize, fontWeight: 800 }}
            inline
          />
        </span>
      );
      return (
        <div style={{ textAlign: block.align || "center" }}>
          {readOnly && block.url ? (
            <a href={block.url} target={block.openInNew ? "_blank" : "_self"} rel="noopener noreferrer">{inner}</a>
          ) : inner}
        </div>
      );
    }
    case "spacer":
      return <div style={{ height: block.height || 40 }}/>;
    case "divider":
      return <hr style={{ borderTop: `${block.thickness || 2}px solid ${block.color || "#E5E7EB"}`, border: 0, borderTopStyle: "solid" }}/>;

    case "columns": {
      const stack = device === "mobile";
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: stack ? "1fr" : `repeat(${block.cols || 2}, 1fr)`,
            gap: block.gap || 24,
          }}
        >
          {(block.columns || []).map((col, idx) => (
            <div key={idx} className={readOnly ? "" : "rounded-md border border-dashed border-slate-300 p-2"}>
              {(col || []).map((child) => (
                <BlockShell
                  key={child.id}
                  block={child}
                  selected={!readOnly && selectedId === child.id}
                  onSelect={readOnly ? undefined : () => setSelectedId(child.id)}
                  onUpdate={readOnly ? undefined : (patch) => updateBlock(child.id, patch)}
                  onRemove={readOnly ? undefined : () => updateBlock(child.id, { __remove: true })}
                  onMove={readOnly ? undefined : () => {}}
                  onDuplicate={readOnly ? undefined : () => {}}
                  readOnly={readOnly}
                  selectedId={selectedId}
                  setSelectedId={setSelectedId}
                  updateBlock={updateBlock}
                  device={device}
                />
              ))}
            </div>
          ))}
        </div>
      );
    }

    case "form": {
      return (
        <div className="rounded-xl border-2 shadow-md p-5 md:p-7"
             style={{ background: block.bgColor || "#EAF6FC", color: block.textColor || "#0F172A", borderColor: `${block.accentColor || ORANGE}55` }}>
          <EditableText
            as="h3"
            value={block.title}
            onChange={(v) => onUpdate && onUpdate({ title: v })}
            readOnly={readOnly}
            style={{ fontFamily: "Georgia, serif", fontSize: 28, fontWeight: 800, textTransform: "uppercase", margin: 0, marginBottom: 8 }}
          />
          <EditableText
            as="p"
            value={block.description}
            onChange={(v) => onUpdate && onUpdate({ description: v })}
            readOnly={readOnly}
            style={{ fontSize: 15, opacity: 0.85, margin: 0, marginBottom: 14 }}
          />
          <div className="space-y-2">
            {(block.fields || ["name","email"]).includes("name") && (
              <input type="text" placeholder="Your first name" disabled
                     className="w-full px-4 py-3 rounded-md border-2 text-sm bg-white"
                     style={{ borderColor: `${block.accentColor || ORANGE}55` }}/>
            )}
            <input type="email" placeholder="Your email" disabled
                   className="w-full px-4 py-3 rounded-md border-2 text-sm bg-white"
                   style={{ borderColor: `${block.accentColor || ORANGE}55` }}/>
            <button type="button" disabled
                    className="w-full px-6 py-3.5 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow"
                    style={{ background: block.accentColor || ORANGE }}>
              <EditableText
                as="span"
                value={block.buttonLabel}
                onChange={(v) => onUpdate && onUpdate({ buttonLabel: v })}
                readOnly={readOnly}
                style={{ color: "#FFFFFF", fontWeight: 800 }}
                inline
              />
            </button>
          </div>
        </div>
      );
    }

    case "testimonial": {
      return (
        <div className="rounded-xl p-5 md:p-7 border-2"
             style={{ background: block.bgColor || "#FFFBEF", color: block.textColor || "#0F172A", borderColor: `${ORANGE}55` }}>
          <div className="flex items-start gap-4 flex-wrap">
            {block.photoUrl ? (
              <img src={resolveSrc(block.photoUrl)} alt="" className="w-16 h-16 rounded-full object-cover border-2" style={{ borderColor: ORANGE }}/>
            ) : null}
            <div className="flex-1 min-w-[200px]">
              <EditableText
                as="div"
                value={block.quote}
                onChange={(v) => onUpdate && onUpdate({ quote: v })}
                readOnly={readOnly}
                style={{ fontFamily: "Georgia, serif", fontSize: 20, fontStyle: "italic", lineHeight: 1.4, marginBottom: 8 }}
              />
              <div className="flex items-center gap-2 text-sm">
                <strong>
                  <EditableText
                    as="span"
                    value={block.author}
                    onChange={(v) => onUpdate && onUpdate({ author: v })}
                    readOnly={readOnly}
                    style={{ fontWeight: 800 }}
                    inline
                  />
                </strong>
                <span style={{ opacity: 0.7 }}>·</span>
                <EditableText
                  as="span"
                  value={block.role}
                  onChange={(v) => onUpdate && onUpdate({ role: v })}
                  readOnly={readOnly}
                  style={{ opacity: 0.8 }}
                  inline
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    case "faq": {
      const items = block.items || [];
      const update = (i, patch) => {
        const next = items.map((it, idx) => idx === i ? { ...it, ...patch } : it);
        onUpdate && onUpdate({ items: next });
      };
      const addItem = () => onUpdate && onUpdate({ items: [...items, { q: "New question?", a: "Answer here." }] });
      const delItem = (i) => onUpdate && onUpdate({ items: items.filter((_, idx) => idx !== i) });
      return (
        <div className="space-y-2">
          {items.map((it, i) => (
            <details key={i} open className="rounded-md border bg-white px-4 py-3" style={{ borderColor: "#E2E8F0" }}>
              <summary className="cursor-pointer font-bold flex items-center gap-2" style={{ color: "#0F172A" }}>
                <EditableText
                  as="span"
                  value={it.q}
                  onChange={(v) => update(i, { q: v })}
                  readOnly={readOnly}
                  style={{ fontWeight: 800 }}
                  inline
                />
                {!readOnly && (
                  <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); delItem(i); }}
                          className="ml-auto text-xs text-red-700">remove</button>
                )}
              </summary>
              <div className="mt-2 text-sm" style={{ color: "#334155" }}>
                <EditableText
                  as="div"
                  value={it.a}
                  onChange={(v) => update(i, { a: v })}
                  readOnly={readOnly}
                  style={{}}
                />
              </div>
            </details>
          ))}
          {!readOnly && (
            <button onClick={addItem}
                    className="text-xs uppercase tracking-[0.18em] font-bold px-3 py-2 rounded border-2"
                    style={{ borderColor: BRAND, color: BRAND, background: "#FFFFFF" }}>
              + Add Question
            </button>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

function resolveSrc(src) {
  if (!src) return src;
  if (src.startsWith("/api/")) return `${BACKEND}${src}`;
  return src;
}

/* ----------------------------- DEEP TREE HELPERS (supports nested columns) ----------------------------- */
function findBlockDeep(list, id) {
  for (const b of list || []) {
    if (b.id === id) return b;
    if (b.type === "columns") {
      for (const col of b.columns || []) {
        const hit = findBlockDeep(col, id);
        if (hit) return hit;
      }
    }
  }
  return null;
}

function mapBlocksDeep(list, fn) {
  return (list || []).map((b) => {
    const mapped = fn(b);
    if (mapped && mapped.type === "columns") {
      return { ...mapped, columns: (mapped.columns || []).map((c) => mapBlocksDeep(c, fn)) };
    }
    return mapped;
  });
}

function updateBlockDeep(list, id, patch) {
  // Special case: remove from column (patch.__remove signals removal)
  if (patch && patch.__remove) {
    return removeBlockDeep(list, id);
  }
  return mapBlocksDeep(list, (b) => (b.id === id ? { ...b, ...patch } : b));
}

function removeBlockDeep(list, id) {
  const out = [];
  for (const b of list || []) {
    if (b.id === id) continue;
    if (b.type === "columns") {
      out.push({ ...b, columns: (b.columns || []).map((c) => removeBlockDeep(c, id)) });
    } else {
      out.push(b);
    }
  }
  return out;
}

function moveBlockDeep(list, id, dir) {
  const i = (list || []).findIndex((b) => b.id === id);
  if (i >= 0) {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const copy = [...list];
    const [item] = copy.splice(i, 1);
    copy.splice(j, 0, item);
    return copy;
  }
  return (list || []).map((b) => {
    if (b.type === "columns") {
      return { ...b, columns: (b.columns || []).map((c) => moveBlockDeep(c, id, dir)) };
    }
    return b;
  });
}

function duplicateBlockDeep(list, id) {
  const i = (list || []).findIndex((b) => b.id === id);
  if (i >= 0) {
    const copy = { ...list[i], id: uid() };
    const next = [...list];
    next.splice(i + 1, 0, copy);
    return next;
  }
  return (list || []).map((b) => {
    if (b.type === "columns") {
      return { ...b, columns: (b.columns || []).map((c) => duplicateBlockDeep(c, id)) };
    }
    return b;
  });
}

/* ----------------------------- INLINE EDITABLE TEXT ----------------------------- */
function EditableText({ value, onChange, style, readOnly, as = "div", inline = false }) {
  const ref = useRef(null);
  const lastExternal = useRef(value);

  // Sync prop -> DOM only when the value really changed from outside.
  useEffect(() => {
    if (ref.current && lastExternal.current !== value && document.activeElement !== ref.current) {
      ref.current.textContent = value || "";
      lastExternal.current = value;
    }
  }, [value]);

  if (readOnly) {
    return renderStaticTag(as, style, value);
  }

  const editableProps = {
    ref,
    contentEditable: true,
    suppressContentEditableWarning: true,
    onInput: (e) => {
      const v = e.currentTarget.textContent || "";
      lastExternal.current = v;
      onChange && onChange(v);
    },
    onBlur: (e) => {
      const v = e.currentTarget.textContent || "";
      lastExternal.current = v;
      onChange && onChange(v);
    },
    onClick: (e) => e.stopPropagation(),
    style: { ...style, outline: "none", display: inline ? "inline" : undefined, cursor: "text" },
    "data-testid": "editable-text",
    children: value,
  };
  return renderEditableTag(as, editableProps);
}

function renderStaticTag(as, style, value) {
  switch (as) {
    case "h1": return <h1 style={style}>{value}</h1>;
    case "h2": return <h2 style={style}>{value}</h2>;
    case "h3": return <h3 style={style}>{value}</h3>;
    case "p":  return <p style={style}>{value}</p>;
    case "span": return <span style={style}>{value}</span>;
    default:   return <div style={style}>{value}</div>;
  }
}

function renderEditableTag(as, props) {
  switch (as) {
    case "h1": return <h1 {...props}/>;
    case "h2": return <h2 {...props}/>;
    case "h3": return <h3 {...props}/>;
    case "p":  return <p {...props}/>;
    case "span": return <span {...props}/>;
    default:   return <div {...props}/>;
  }
}

/* ----------------------------- BLOCK PROPERTIES PANEL ----------------------------- */
function BlockProps({ block, onChange }) {
  const t = block.type;
  return (
    <div data-testid="block-props">
      <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-3" style={{ color: BRAND }}>
        ★ {t.toUpperCase()} BLOCK
      </div>

      {(t === "heading" || t === "text") && (
        <>
          <NumField label="Font size (px)" value={block.fontSize} onChange={(v) => onChange({ fontSize: v })} testid="props-fontsize"/>
          <ColorField label="Text color" value={block.color} onChange={(v) => onChange({ color: v })} testid="props-color"/>
          <PickerRow label="Align" value={block.align} onChange={(v) => onChange({ align: v })}
                     options={[{v:"left",l:"L"},{v:"center",l:"C"},{v:"right",l:"R"}]} testid="props-align"/>
          <PickerRow label="Font" value={block.fontFamily} onChange={(v) => onChange({ fontFamily: v })}
                     options={[{v:"serif",l:"Serif"},{v:"sans",l:"Sans"}]} testid="props-font"/>
          {t === "heading" && (
            <PickerRow label="Style" value={block.bold ? "bold" : "normal"} onChange={(v) => onChange({ bold: v === "bold" })}
                       options={[{v:"normal",l:"Normal"},{v:"bold",l:"Bold"}]} testid="props-bold"/>
          )}
          {t === "heading" && (
            <PickerRow label="Case" value={block.uppercase ? "upper" : "normal"} onChange={(v) => onChange({ uppercase: v === "upper" })}
                       options={[{v:"normal",l:"Mixed"},{v:"upper",l:"UPPER"}]} testid="props-case"/>
          )}
        </>
      )}

      {t === "image" && (
        <>
          <ImageUploadField label="Upload image from your computer" value={block.src} onChange={(v) => onChange({ src: v })} testid="props-img-src"/>
          <NumField label="Width %" value={block.widthPct} onChange={(v) => onChange({ widthPct: v })} testid="props-img-w"/>
          <NumField label="Corner radius (px)" value={block.radius} onChange={(v) => onChange({ radius: v })} testid="props-img-r"/>
          <PickerRow label="Align" value={block.align} onChange={(v) => onChange({ align: v })}
                     options={[{v:"left",l:"L"},{v:"center",l:"C"},{v:"right",l:"R"}]} testid="props-img-align"/>
        </>
      )}

      {t === "video" && (
        <>
          <TextField label="YouTube URL (UNLISTED)" value={block.url} onChange={(v) => onChange({ url: v })} testid="props-video-url"/>
          <p className="text-[11px] text-foreground/60 mt-1">
            Set your YouTube video to <strong>Unlisted</strong> before pasting.
          </p>
        </>
      )}

      {t === "button" && (
        <>
          <TextField label="Button label" value={block.label} onChange={(v) => onChange({ label: v })} testid="props-btn-label"/>
          <TextField label="Click goes to URL" value={block.url} onChange={(v) => onChange({ url: v })} testid="props-btn-url"/>
          <ColorField label="Background color" value={block.bgColor} onChange={(v) => onChange({ bgColor: v })} testid="props-btn-bg"/>
          <ColorField label="Text color" value={block.textColor} onChange={(v) => onChange({ textColor: v })} testid="props-btn-fg"/>
          <NumField label="Font size (px)" value={block.fontSize} onChange={(v) => onChange({ fontSize: v })} testid="props-btn-size"/>
          <PickerRow label="Align" value={block.align} onChange={(v) => onChange({ align: v })}
                     options={[{v:"left",l:"L"},{v:"center",l:"C"},{v:"right",l:"R"}]} testid="props-btn-align"/>
        </>
      )}

      {t === "spacer" && (
        <NumField label="Height (px)" value={block.height} onChange={(v) => onChange({ height: v })} testid="props-spacer-h"/>
      )}

      {t === "divider" && (
        <>
          <NumField label="Thickness (px)" value={block.thickness} onChange={(v) => onChange({ thickness: v })} testid="props-div-t"/>
          <ColorField label="Color" value={block.color} onChange={(v) => onChange({ color: v })} testid="props-div-c"/>
        </>
      )}

      {t === "columns" && (
        <>
          <PickerRow label="Number of columns" value={String(block.cols || 2)} onChange={(v) => {
            const n = parseInt(v, 10);
            const cur = block.columns || [];
            let next = cur.slice(0, n);
            while (next.length < n) next.push([]);
            onChange({ cols: n, columns: next });
          }}
            options={[{v:"2",l:"2"},{v:"3",l:"3"}]} testid="props-cols-n"/>
          <NumField label="Gap (px)" value={block.gap} onChange={(v) => onChange({ gap: v })} testid="props-cols-gap"/>
          <p className="text-[11px] text-foreground/60 mt-1">
            Click a block inside any column to edit it. Add new blocks from the left panel — drag them into a column.
          </p>
        </>
      )}

      {t === "form" && (
        <>
          <TextField label="Form title" value={block.title} onChange={(v) => onChange({ title: v })} testid="props-form-title"/>
          <TextField label="Description" value={block.description} onChange={(v) => onChange({ description: v })} testid="props-form-desc"/>
          <TextField label="Button text" value={block.buttonLabel} onChange={(v) => onChange({ buttonLabel: v })} testid="props-form-btn"/>
          <ColorField label="Background color" value={block.bgColor} onChange={(v) => onChange({ bgColor: v })} testid="props-form-bg"/>
          <ColorField label="Accent (button) color" value={block.accentColor} onChange={(v) => onChange({ accentColor: v })} testid="props-form-accent"/>
          <PickerRow label="Fields" value={(block.fields || []).includes("name") ? "both" : "email"} onChange={(v) => onChange({ fields: v === "both" ? ["name","email"] : ["email"] })}
                     options={[{v:"both",l:"Name + Email"},{v:"email",l:"Email only"}]} testid="props-form-fields"/>
          <p className="text-[11px] text-foreground/60 mt-1">
            Submissions go straight into your contacts list (Members → Contacts).
          </p>
        </>
      )}

      {t === "testimonial" && (
        <>
          <ImageUploadField label="Customer photo (optional)" value={block.photoUrl} onChange={(v) => onChange({ photoUrl: v })} testid="props-tst-photo"/>
          <ColorField label="Background color" value={block.bgColor} onChange={(v) => onChange({ bgColor: v })} testid="props-tst-bg"/>
          <ColorField label="Text color" value={block.textColor} onChange={(v) => onChange({ textColor: v })} testid="props-tst-fg"/>
          <p className="text-[11px] text-foreground/60 mt-1">
            Click the quote / name / role on the page to edit.
          </p>
        </>
      )}

      {t === "faq" && (
        <p className="text-[11px] text-foreground/60">
          Click any question or answer on the page to edit. Use the <strong>+ Add Question</strong> button on the page to add more.
        </p>
      )}
    </div>
  );
}

/* ----------------------------- IMAGE UPLOAD FIELD ----------------------------- */
function ImageUploadField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [picker, setPicker] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("That's not an image."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data?.url || "");
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    }
    setUploading(false);
  };

  return (
    <div className="mb-3" data-testid={testid}>
      <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</label>
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
             onChange={(e) => handleFile(e.target.files?.[0])}/>
      <div className="grid grid-cols-3 gap-1.5">
        <button type="button" onClick={() => inputRef.current?.click()}
                disabled={uploading}
                data-testid={`${testid}-btn`}
                className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded border-2 text-[10px] uppercase tracking-[0.16em] font-bold disabled:opacity-50"
                style={{ borderColor: BRAND, color: BRAND, background: "#FFFFFF" }}>
          <Upload className="w-3.5 h-3.5"/> {uploading ? "…" : "Upload"}
        </button>
        <button type="button" onClick={() => setPicker("stock")}
                data-testid={`${testid}-stock-btn`}
                className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded border-2 text-[10px] uppercase tracking-[0.16em] font-bold"
                style={{ borderColor: ORANGE, color: ORANGE, background: "#FFFFFF" }}>
          <ImageIcon className="w-3.5 h-3.5"/> Stock
        </button>
        <button type="button" onClick={() => setPicker("ai")}
                data-testid={`${testid}-ai-btn`}
                className="inline-flex items-center justify-center gap-1.5 px-2 py-2 rounded border-2 text-[10px] uppercase tracking-[0.16em] font-bold text-white"
                style={{ borderColor: GREEN, background: GREEN }}>
          ★ AI Make
        </button>
      </div>
      <div className="text-[10px] text-foreground/50 mt-1">Or paste an image URL:</div>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
             placeholder="https://…"
             data-testid={`${testid}-url`}
             className="w-full mt-1 px-2.5 py-2 rounded border-2 text-xs font-mono outline-none focus:ring-2 focus:ring-[#F39A1F]"
             style={{ borderColor: "#E5E7EB" }}/>
      {value && (
        <div className="mt-2 rounded overflow-hidden border" style={{ borderColor: "#E5E7EB" }}>
          <img src={resolveSrc(value)} alt="" className="w-full h-20 object-cover"/>
        </div>
      )}
      {picker === "stock" && (
        <StockPhotoPicker
          onPick={(url) => { onChange(url); setPicker(false); }}
          onClose={() => setPicker(false)}
        />
      )}
      {picker === "ai" && (
        <AIImagePicker
          onPick={(url) => { onChange(url); setPicker(false); }}
          onClose={() => setPicker(false)}
        />
      )}
    </div>
  );
}

function AIImagePicker({ onPick, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState("");

  const generate = async (e) => {
    if (e) e.preventDefault();
    if (prompt.trim().length < 3) { toast.error("Tell it what to make."); return; }
    setBusy(true);
    setImageUrl("");
    try {
      const r = await api.post("/images/generate", { prompt: prompt.trim() }, { timeout: 90000 });
      setImageUrl(r.data?.url || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't make the image.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-picker">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center gap-3" style={{ background: "#EAF6FC" }}>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold flex-1" style={{ color: GREEN }}>★ MAKE A PRODUCT IMAGE</div>
          <button onClick={onClose} className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "#0F172A", color: "#FFFFFF" }}>
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-5 flex-1 overflow-y-auto">
          <form onSubmit={generate}>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>
              Describe the image you want
            </label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
                      placeholder='e.g. "A clean white bottle of apple-cider-vinegar gummies on a marble kitchen counter, soft daylight, no text on the label"'
                      data-testid="ai-picker-prompt"
                      className="w-full px-3 py-2 rounded border-2 text-sm outline-none focus:ring-2 focus:ring-[#F39A1F]"
                      style={{ borderColor: "#E5E7EB" }}/>
            <button type="submit" disabled={busy}
                    data-testid="ai-picker-go"
                    className="mt-3 w-full px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] text-white disabled:opacity-50"
                    style={{ background: GREEN }}>
              {busy ? "Making it (~20 sec)…" : "★ Make This Image"}
            </button>
          </form>
          {imageUrl && (
            <div className="mt-5">
              <div className="rounded overflow-hidden border-2 mb-3" style={{ borderColor: ORANGE }}>
                <img src={resolveSrc(imageUrl)} alt="" className="w-full"/>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => onPick(imageUrl)}
                        data-testid="ai-picker-use"
                        className="flex-1 px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] text-white"
                        style={{ background: GREEN }}>
                  Use This Image
                </button>
                <button type="button" onClick={() => generate()}
                        className="px-5 py-3 rounded text-sm font-bold uppercase tracking-[0.18em] border-2"
                        style={{ borderColor: BRAND, color: BRAND, background: "#FFFFFF" }}>
                  Try Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StockPhotoPicker({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (query) => {
    setBusy(true);
    try {
      const r = await api.get("/photos/search", { params: { q: query, per_page: 24 } });
      setPhotos(r.data?.photos || []);
    } catch {
      toast.error("Stock search failed.");
    }
    setBusy(false);
  };

  useEffect(() => { search(""); /* curated on open */ }, []);

  const submit = (e) => { e.preventDefault(); search(q); };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="stock-picker">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b flex items-center gap-3 flex-wrap" style={{ background: "#EAF6FC" }}>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold" style={{ color: BRAND }}>★ STOCK PHOTOS</div>
          <form onSubmit={submit} className="flex-1 flex items-center gap-2 min-w-[220px]">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search photos — e.g. seniors walking, kitchen, prayer hands"
                   data-testid="stock-picker-q"
                   className="flex-1 px-3 py-2 rounded border-2 text-sm outline-none focus:ring-2 focus:ring-[#F39A1F]"
                   style={{ borderColor: `${BRAND}55` }}/>
            <button type="submit" data-testid="stock-picker-search"
                    className="px-4 py-2 rounded text-xs uppercase tracking-[0.18em] font-bold text-white"
                    style={{ background: GREEN }}>Search</button>
          </form>
          <button onClick={onClose} className="w-8 h-8 rounded flex items-center justify-center" style={{ background: "#0F172A", color: "#FFFFFF" }}>
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {busy ? (
            <div className="text-center py-12 text-sm text-foreground/60">Loading photos…</div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12 text-sm text-foreground/60">No photos. Try a different search.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {photos.map((p) => (
                <button key={p.id} type="button" onClick={() => onPick(p.preview || p.thumb || p.download)}
                        data-testid={`stock-photo-${p.id}`}
                        className="relative group rounded overflow-hidden border-2 hover:border-[#F39A1F]"
                        style={{ borderColor: "#E5E7EB", aspectRatio: "4 / 3" }}>
                  <img src={p.thumb} alt={p.alt || ""} className="w-full h-full object-cover"/>
                  <div className="absolute inset-x-0 bottom-0 px-1.5 py-0.5 text-[9px] text-white bg-black/55 truncate">
                    {p.photographer ? `© ${p.photographer}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-2 border-t text-[10px] text-foreground/60 text-center">
          Photos provided by Pexels — free to use, no attribution required.
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- SMALL UI HELPERS ----------------------------- */
function AddBtn({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className="flex flex-col items-center justify-center gap-1.5 py-3 rounded-md border-2 bg-white hover:shadow transition"
            style={{ borderColor: "#CBD5E1" }}>
      <div style={{ color: BRAND }}>{icon}</div>
      <div className="text-[10px] tracking-[0.18em] uppercase font-bold" style={{ color: "#0F172A" }}>{label}</div>
    </button>
  );
}

function SlugInput({ value, onChange, disabled }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-70" style={{ color: "#EAF6FC" }}>★ Your Link Name:</span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="your-link-name"
        data-testid="wysiwyg-slug"
        className="px-2 py-1.5 rounded text-xs bg-white text-[#0F172A] font-mono w-32 outline-none focus:ring-2 focus:ring-[#F39A1F] disabled:opacity-70"
      />
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</label>
      <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
             data-testid={testid}
             className="w-full px-2.5 py-2 rounded border-2 text-sm outline-none focus:ring-2 focus:ring-[#F39A1F]"
             style={{ borderColor: "#E5E7EB" }}/>
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</label>
      <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value) || 0)}
             data-testid={testid}
             className="w-full px-2.5 py-2 rounded border-2 text-sm outline-none focus:ring-2 focus:ring-[#F39A1F]"
             style={{ borderColor: "#E5E7EB" }}/>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</label>
      <div className="flex items-center gap-2" data-testid={testid}>
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value.toUpperCase())}
               className="w-10 h-9 rounded cursor-pointer border" style={{ borderColor: "#E5E7EB" }}/>
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
               className="flex-1 px-2 py-2 rounded border-2 text-xs font-mono outline-none focus:ring-2 focus:ring-[#F39A1F]"
               style={{ borderColor: "#E5E7EB" }}/>
      </div>
    </div>
  );
}

function PickerRow({ label, value, onChange, options, testid }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</label>
      <div className="flex gap-1" data-testid={testid}>
        {options.map((o) => {
          const sel = value === o.v;
          return (
            <button key={o.v} type="button" onClick={() => onChange(o.v)}
                    data-testid={`${testid}-${o.v}`}
                    className="flex-1 px-2 py-1.5 rounded text-[11px] uppercase tracking-[0.18em] font-bold border-2"
                    style={{
                      background: sel ? "#0F172A" : "#FFFFFF",
                      color: sel ? "#FFFFFF" : "#0F172A",
                      borderColor: sel ? "#0F172A" : "#E5E7EB",
                    }}>{o.l}</button>
          );
        })}
      </div>
    </div>
  );
}

function SwatchRow({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)}
                data-testid={`${testid}-${c.replace("#","")}`}
                className="w-8 h-8 rounded border-2 transition hover:scale-110"
                title={c}
                style={{
                  background: c,
                  borderColor: value?.toUpperCase() === c.toUpperCase() ? "#F39A1F" : "#E5E7EB",
                  boxShadow: value?.toUpperCase() === c.toUpperCase() ? "0 0 0 2px #F39A1F55" : undefined,
                }}/>
      ))}
    </div>
  );
}

/* ----------------------------- UTILS ----------------------------- */
function extractYouTubeId(u) {
  const url = (u || "").trim();
  if (!url) return null;
  const re = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/;
  const match = url.match(re);
  return match ? match[1] : null;
}
